// Figtree = mobile banner font, to match the Branding settings preview typography
import '@fontsource/figtree/latin-400.css'
import '@fontsource/figtree/latin-600.css'

import {
  Anchor,
  Box,
  Button,
  Center,
  Group,
  Image,
  Loader,
  Stack,
  Text,
  TextInput,
  Title,
  VisuallyHidden,
} from '@mantine/core'
import { hasLength, isEmail, isNotEmpty, useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useParams } from 'wouter'

import logoImage from '@/assets/images/Logo-light-punchout.png'
import type { RouteParams } from '@/configs/routes'
import { useGlobalState } from '@/hooks/useGlobalState'
import { FriendlyCaptcha, type FriendlyCaptchaRef } from '@/screens/OnboardingScreen/FriendlyCaptcha'
import {
  MOBILE_BANNER_FONT_FAMILY,
  MOBILE_BANNER_SUBTITLE_FONT_SIZE,
  MOBILE_BANNER_SUBTITLE_LETTER_SPACING,
  MOBILE_BANNER_TITLE_FONT_SIZE,
  MOBILE_BANNER_TITLE_LETTER_SPACING,
} from '@/screens/SetupScreen/brandingMobileLayout'
import type { GetChannelPublicInfoResponse } from '@/services/api/api.types'
import * as styles from '@/styles/OnboardingScreen.css'
import { ERROR_MESSAGES, MIN_NAME_LENGTH } from '@/utils/onboardingConstants'
import { handleOnboardingError, showOnboardingErrorNotification } from '@/utils/onboardingErrorHandling'

type RegistrationFormValues = {
  name: string
  email: string
  confirmEmail: string
}

// brand_image may be a full URL (/channel_info) or a host-less signed fragment
// (/onboarding/channel). Only the former is usable on a logged-out page.
function resolveBrandImageUrl(brandImage: string | null, serverBaseUrl: string | undefined): string | null {
  if (!brandImage) return null
  if (/^https?:\/\//i.test(brandImage)) return brandImage
  if (serverBaseUrl) return `${serverBaseUrl}/files/${brandImage}`
  return null
}

// Mirrors the BrandBannerPreview capsule from Branding settings.
function ChannelBrandHeader({ channel, imageUrl }: { channel: GetChannelPublicInfoResponse; imageUrl: string | null }) {
  const title = channel.brandTitle || channel.name
  const hasImage = !!imageUrl

  return (
    <Box
      style={{
        borderRadius: 999,
        padding: 10,
        minHeight: 48,
        width: '100%',
        maxWidth: 420,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: hasImage ? 'flex-start' : 'center',
        backgroundColor: 'var(--mantine-color-gray-1)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        border: '1px solid var(--mantine-color-gray-2)',
      }}>
      {hasImage && (
        <Box style={{ width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
          <Image src={imageUrl} alt={`${title} logo`} w={56} h={56} fit="cover" />
        </Box>
      )}
      <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
        <Text
          truncate
          style={{
            fontSize: MOBILE_BANNER_TITLE_FONT_SIZE,
            fontFamily: MOBILE_BANNER_FONT_FAMILY,
            fontWeight: 600,
            letterSpacing: MOBILE_BANNER_TITLE_LETTER_SPACING,
            textTransform: 'uppercase',
            lineHeight: '18px',
            textAlign: hasImage ? 'left' : 'center',
          }}>
          {title}
        </Text>
        {channel.brandSubtitle && (
          <Text
            truncate
            style={{
              fontSize: MOBILE_BANNER_SUBTITLE_FONT_SIZE,
              fontFamily: MOBILE_BANNER_FONT_FAMILY,
              fontWeight: 400,
              letterSpacing: MOBILE_BANNER_SUBTITLE_LETTER_SPACING,
              textTransform: 'uppercase',
              lineHeight: '15px',
              opacity: 0.75,
              textAlign: hasImage ? 'left' : 'center',
            }}>
            {channel.brandSubtitle}
          </Text>
        )}
      </Stack>
    </Box>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <VisuallyHidden component="h1">Register for a Sidekick channel</VisuallyHidden>
        <header className={styles.header}>
          <img src={logoImage} alt="Sidekick Platform" className={styles.logo} />
        </header>

        <div className={styles.stepperCard}>
          <div className={styles.stepContent}>{children}</div>
        </div>
      </div>
    </div>
  )
}

export function ChannelRegistrationScreen() {
  const { api, configStore } = useGlobalState()
  const { channelId } = useParams<RouteParams['channelRegistration']>()

  const [captchaResponse, setCaptchaResponse] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)
  const captchaRef = useRef<FriendlyCaptchaRef>(null)

  const captchaSitekey = configStore.captchaSitekey || 'test-sitekey'

  const channelQuery = useQuery({
    queryKey: ['channel', 'public', channelId],
    queryFn: () => api.getChannelPublicInfo(channelId),
    retry: false,
  })

  // Only /channel_info returns a full-URL brand_image; fetched purely for the logo.
  const channelInfoQuery = useQuery({
    queryKey: ['channel', 'info-public', channelId],
    queryFn: () => api.getChannelInfo(channelId),
    retry: false,
  })

  const form = useForm<RegistrationFormValues>({
    initialValues: { name: '', email: '', confirmEmail: '' },
    validate: {
      name: hasLength({ min: MIN_NAME_LENGTH }, ERROR_MESSAGES.CONTACT_NAME_TOO_SHORT),
      email: isEmail(ERROR_MESSAGES.EMAIL_INVALID),
      confirmEmail: (value, values) => {
        if (!isNotEmpty(value)) return ERROR_MESSAGES.CONFIRM_EMAIL_REQUIRED
        if (value !== values.email) return ERROR_MESSAGES.EMAIL_MISMATCH
        return null
      },
    },
    validateInputOnBlur: true,
  })

  const registerMutation = useMutation({
    mutationFn: (values: RegistrationFormValues) =>
      api.registerEndUser({
        channel_id: channelId,
        name: values.name,
        email: values.email,
        'frc-captcha-response': captchaResponse ?? '',
      }),
    onSuccess: () => {
      setIsComplete(true)
    },
    onError: error => {
      const onboardingError = handleOnboardingError(error, 'register')
      showOnboardingErrorNotification(onboardingError, 'registration')
      setCaptchaResponse(null)
      captchaRef.current?.reset()
    },
  })

  const handleSubmit = (values: RegistrationFormValues) => {
    if (!captchaResponse) {
      notifications.show({
        title: 'Captcha Required',
        message: ERROR_MESSAGES.CAPTCHA_REQUIRED,
        color: 'red',
      })
      return
    }
    registerMutation.mutate(values)
  }

  if (channelQuery.isLoading) {
    return (
      <Shell>
        <Center mih={400}>
          <Loader />
        </Center>
      </Shell>
    )
  }

  if (channelQuery.isError || !channelQuery.data) {
    return (
      <Shell>
        <Stack gap="md" mt="xl" maw={560} mx="auto" ta="center">
          <Title order={4}>Channel not found</Title>
          <Text c="dimmed">
            We couldn&apos;t find the channel you&apos;re looking for. Please double-check the link or contact the
            channel administrator.
          </Text>
        </Stack>
      </Shell>
    )
  }

  const channel = channelQuery.data

  if (!channel.selfRegistrationEnabled) {
    return (
      <Shell>
        <Stack gap="md" mt="xl" maw={560} mx="auto" ta="center">
          <Title order={4}>Registration unavailable</Title>
          <Text c="dimmed">
            Self-registration is not currently available for {channel.brandTitle || channel.name}. Please contact the
            channel administrator for access.
          </Text>
        </Stack>
      </Shell>
    )
  }

  const brandImageUrl = resolveBrandImageUrl(
    channelInfoQuery.data?.brandImage ?? channel.brandImage,
    configStore.serverBaseUrl,
  )

  if (isComplete) {
    return (
      <Shell>
        <Stack gap="lg" mt="xl" maw={560} mx="auto" align="center">
          <ChannelBrandHeader channel={channel} imageUrl={brandImageUrl} />
          <Stack gap="xs" ta="center" align="center">
            <IconCheck size={48} color="var(--mantine-color-teal-6)" />
            <Title order={4}>You&apos;re all set!</Title>
            <Text c="dimmed">Check your email for instructions to access the channel.</Text>
          </Stack>
        </Stack>
      </Shell>
    )
  }

  return (
    <Shell>
      <Stack gap="md" mt="xl" maw={560} mx="auto">
        <ChannelBrandHeader channel={channel} imageUrl={brandImageUrl} />
        <Text size="sm" c="dimmed" ta="center">
          Register below to join the channel.
        </Text>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md" my="xl">
            <TextInput label="Name" placeholder="Your full name" required {...form.getInputProps('name')} />
            <TextInput
              label="Email"
              placeholder="your.email@company.com"
              type="email"
              required
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Confirm Email"
              placeholder="Confirm your email address"
              type="email"
              required
              {...form.getInputProps('confirmEmail')}
            />

            <Group mt="md">
              <FriendlyCaptcha
                ref={captchaRef}
                sitekey={captchaSitekey}
                onVerify={setCaptchaResponse}
                onError={() => {
                  notifications.show({
                    title: 'Captcha Error',
                    message: ERROR_MESSAGES.CAPTCHA_ERROR,
                    color: 'red',
                  })
                }}
              />
            </Group>

            <Text size="sm" c="dimmed" mt="md">
              By registering, you agree to the{' '}
              <Anchor href="https://sidekickwellness.com/tos.html" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </Anchor>
              . For more information, see the{' '}
              <Anchor href="https://sidekickwellness.com/privacy.html" target="_blank" rel="noopener noreferrer">
                Sidekick Wellness Privacy Statement
              </Anchor>
              .
            </Text>

            <Group justify="flex-end" mt="xl">
              <Button type="submit" loading={registerMutation.isPending} disabled={!form.isValid() || !captchaResponse}>
                Register
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Shell>
  )
}
